/**
 * Whether this branch's trading has reached head office.
 *
 * WHAT IT IS FOR
 * --------------
 * Being offline is normal here and not worth alarming anyone about — the till
 * keeps working, and records wait in a queue. What a manager needs to know is
 * whether that queue is draining. A number that has been climbing all day is
 * the thing worth noticing, and it has no other symptom: every screen looks
 * completely normal while a week of takings sits unsent.
 *
 * So this stays quiet when things are fine, says how many are waiting when
 * some are, and only turns red when records have failed enough times to have
 * stopped being retried.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { SyncProblems } from './SyncProblems.jsx';

export function SyncIndicator() {
  const { data, isError } = useQuery({
    queryKey: ['sync', 'status'],
    queryFn: () => api.get('/sync/status'),

    // Often enough that a manager sees a queue draining, rarely enough that it
    // costs nothing. This is a local call on the branch network.
    refetchInterval: 15_000,

    // Failing to reach the branch machine is itself the answer, not an error
    // worth retrying hard.
    retry: false,
  });

  const [open, setOpen] = useState(false);

  if (isError || !data) return null;

  const { queue, cloud, healthy } = data;
  const waiting = (queue?.pending ?? 0) + (queue?.failed ?? 0);
  const stuck = queue?.stuck ?? 0;

  // No cloud configured is a legitimate way to run a single shop. Showing a
  // permanent warning for a deliberate choice teaches people to ignore it.
  if (!cloud?.configured) return null;

  const state = stuck > 0 ? 'stuck' : healthy ? 'synced' : 'waiting';

  const look = {
    synced: 'text-ink-3',
    waiting: 'bg-warn-bg text-warn',
    stuck: 'bg-bad-bg text-bad',
  }[state];

  const dot = {
    synced: 'bg-good',
    waiting: 'bg-warn',
    stuck: 'bg-bad',
  }[state];

  const words = {
    synced: 'All sent to head office',
    waiting: `${waiting} record${waiting === 1 ? '' : 's'} waiting to reach head office`,
    stuck: `${stuck} record${stuck === 1 ? '' : 's'} could not be sent — tell support`,
  }[state];

  // The dot alone means nothing to a screen reader, and the colour alone means
  // nothing to anyone who cannot distinguish it — so both carry the words too.
  const shell =
    'hidden shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium sm:inline-flex';
  const content = (
    <>
      <span aria-hidden="true" className={`size-1.5 rounded-full ${dot}`} />
      {state === 'synced' ? 'Synced' : state === 'waiting' ? `${waiting} waiting` : `${stuck} stuck`}
    </>
  );

  // Only the stuck state has anything to be done about it: the panel is where
  // those records are retried or cleared. Waiting and synced are just saying
  // so, and stay plain text.
  if (state !== 'stuck') {
    return (
      <span title={words} aria-label={words} className={`${shell} ${look}`}>
        {content}
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        title={words}
        aria-label={`${words}. Open sync problems`}
        onClick={() => setOpen(true)}
        className={`${shell} ${look} hover:opacity-90`}
      >
        {content}
      </button>
      {open && <SyncProblems onClose={() => setOpen(false)} />}
    </>
  );
}
